import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { renderButton } from '../utils/uiUtils';
import { BookmarkIcon, TrashIcon, ArrowLeftIcon } from '@heroicons/react/outline';
import { FaRobot, FaComments } from 'react-icons/fa';
import Tooltip from './Tooltip';
import { paperApi } from '../utils/apiUtils';
import StreamingSummary from './StreamingSummary';
import ChatPanel from './ChatPanel';
import MarkdownRenderer from './MarkdownRenderer';
import { SOURCE_NAME } from '../constants/tagConstants';

function PaperInformation() {
	const [paper, setPaper] = useState(null);
	const [notFound, setNotFound] = useState(false);
	const [fetching, setFetching] = useState(false);
	// `showSummary` and `showChat` start false — even for papers the user
	// has already been charged for. Mounting is strictly opt-in now, so
	// opening the page never triggers a network call to /ai/summary or
	// /ai/chat/sessions. This is what stops the chat quota from silently
	// incrementing when someone just browses to a paper.
	const [showSummary, setShowSummary] = useState(false);
	const [showChat, setShowChat] = useState(false);
	const [isFavorite, setIsFavorite] = useState(false);
	const location = useLocation();
	// Extract paper ID from the path — supports IDs with slashes (bioRxiv DOIs)
	const id = location.pathname.replace(/^\/paper\//, '');
	const {
		user,
		addFavoritePaper,
		removeFavoritePaper,
		refreshUser,
		eligibility,
		aiSummarizedPaperIds,
		aiChattedPaperIds
	} = useAuth();
	const { from } = location.state || { from: { pathname: "/today" } };
	const isLoggedIn = user !== null;
	const alreadySummarized = aiSummarizedPaperIds.includes(id);
	const alreadyChatted = aiChattedPaperIds.includes(id);

	useEffect(() => {
		const fetchPaper = async () => {
			setNotFound(false);
			setFetching(true);
			try {
				const { paper } = await paperApi.lookupPaper(id);
				setPaper(paper);
			} catch (error) {
				if (error.response?.status === 404) {
					setNotFound(true);
				}
				console.error('Error fetching paper:', error);
			} finally {
				setFetching(false);
			}
		};
		if (id) fetchPaper();
	}, [id]);

	useEffect(() => {
		setIsFavorite(user?.favoritePapers?.includes(id));
	}, [user, id]);

	const handleAdd = () => { if (!isLoggedIn || !eligibility.favoritePaperAllowed) return; addFavoritePaper(id); setIsFavorite(true); };
	const handleRemove = () => { if (!isLoggedIn) return; removeFavoritePaper(id); setIsFavorite(false); };

	// Eligibility gating for the AI action buttons. A paper the user has
	// already been charged for is always re-openable regardless of the
	// current quota state (re-reading is free server-side).
	const canSummary = isLoggedIn && (alreadySummarized || eligibility.aiSummaryAllowed);
	const canChat = isLoggedIn && (alreadyChatted || eligibility.aiChatAllowed);
	const summaryDisabledReason = !isLoggedIn
		? 'Login required'
		: (alreadySummarized ? '' : eligibility.aiSummaryNotAllowedReason || '');
	const chatDisabledReason = !isLoggedIn
		? 'Login required'
		: (alreadyChatted ? '' : eligibility.aiChatNotAllowedReason || '');

	const handleShowSummary = () => {
		if (!canSummary || !paper || showSummary) return;
		setShowSummary(true);
	};

	const handleShowChat = () => {
		if (!canChat || !paper || showChat) return;
		setShowChat(true);
	};

	const handleSummaryDone = () => {
		if (!alreadySummarized) refreshUser();
	};

	// Smart button labels — reflect both "already counted" state and the
	// currently-open state so the user always knows what's going to happen.
	const summaryLabel = showSummary
		? 'Summary Opened'
		: alreadySummarized
			? 'View AI Summary'
			: 'Generate AI Summary';
	const chatLabel = showChat
		? 'Chat Opened'
		: alreadyChatted
			? 'Open Chat'
			: 'Start Chat';

	if (notFound) {
		return (
			<div className="max-w-md mx-auto py-16 text-center">
				<div className="text-white/20 text-4xl mb-3 font-bold">?</div>
				<p className="text-white/60 text-sm font-semibold mb-1">Paper not found</p>
				<p className="text-white/30 text-xs mb-6">
					Could not find a paper with ID "<span className="text-white/50 font-mono">{id}</span>" on {SOURCE_NAME}.
				</p>
				<Link
					to={from}
					className="inline-flex items-center bg-white/10 text-white/70 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-white/20 transition-colors"
				>
					<ArrowLeftIcon className="w-3.5 h-3.5 mr-1.5" />
					Go back
				</Link>
			</div>
		);
	}

	if (!paper) {
		return (
			<div className="max-w-4xl mx-auto py-16 text-center">
				<div className="w-6 h-6 border-2 border-white/20 border-t-yellow-300 rounded-full animate-spin mx-auto mb-4" />
				<p className="text-white/30 text-xs">
					{fetching ? `Looking up paper on ${SOURCE_NAME}...` : 'Loading paper...'}
				</p>
			</div>
		);
	}

	return (
		<div className="max-w-3xl mx-auto py-4 sm:py-8">
			<div className="bg-white rounded-2xl shadow-card-hover overflow-hidden border border-gray-100">
				{/* Top bar */}
				<div className="h-1 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-400" />

				<div className="p-6 sm:p-10">
					{/* Tags */}
					<div className="flex flex-wrap gap-1.5 mb-5">
						<span className="text-[10px] font-semibold uppercase tracking-wider text-purple-600 bg-purple-50 px-2 py-0.5 rounded">{paper.topic}</span>
						<span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-50 px-2 py-0.5 rounded">{paper.subtopic}</span>
					</div>

					<h2 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900 leading-snug tracking-tight">{paper.title}</h2>

					{/* Metadata */}
					<div className="flex flex-wrap gap-6 mb-8 text-xs text-gray-500">
						<div className="flex-1 min-w-0">
							<span className="text-[10px] text-gray-300 uppercase tracking-widest block mb-1">Authors</span>
							<p className="text-gray-700 font-medium leading-relaxed">
								{(() => {
									const authors = paper.Authors?.map(a => a.author) || [paper.first_author || 'Unknown'];
									const shown = authors.slice(0, 3);
									return shown.join(', ') + (authors.length > 3 ? `, +${authors.length - 3} more` : '');
								})()}
							</p>
						</div>
						<div className="shrink-0">
							<span className="text-[10px] text-gray-300 uppercase tracking-widest block mb-1">Published</span>
							<span className="text-gray-700 font-medium tabular-nums">{new Date(paper.publish_time).toLocaleDateString()}</span>
						</div>
					</div>

					{/* Abstract */}
					<div className="mb-8">
						<h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-3">Abstract</h3>
						<MarkdownRenderer
							content={paper.abstract}
							className="text-sm text-gray-600"
						/>
					</div>

					{/* AI Tools — both summary and chat are opt-in. Mounting
					     either child component fires a server call, so we keep
					     them collapsed until the user explicitly clicks a
					     button. Labels adapt based on whether the user has
					     already been charged for this paper. */}
					<div className="mb-8">
						<h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-3">AI Tools</h3>
						<div className="flex flex-wrap gap-2 mb-4">
							<Tooltip content={summaryDisabledReason}>
								<button
									type="button"
									onClick={handleShowSummary}
									disabled={!canSummary || showSummary}
									className={`inline-flex items-center px-3.5 py-2 text-[11px] font-semibold text-white rounded-lg transition-all duration-200 ${
										showSummary
											? 'bg-purple-300 cursor-default'
											: 'bg-purple-600 hover:bg-purple-700'
									} ${(!canSummary && !showSummary) ? 'opacity-30 cursor-not-allowed hover:bg-purple-600' : ''}`}
								>
									<FaRobot className="w-3 h-3 mr-1.5" />
									{summaryLabel}
									{alreadySummarized && !showSummary && (
										<span className="ml-1.5 px-1 py-0.5 bg-purple-500 rounded text-[9px] font-bold uppercase tracking-wider">Cached</span>
									)}
								</button>
							</Tooltip>
							<Tooltip content={chatDisabledReason}>
								<button
									type="button"
									onClick={handleShowChat}
									disabled={!canChat || showChat}
									className={`inline-flex items-center px-3.5 py-2 text-[11px] font-semibold text-white rounded-lg transition-all duration-200 ${
										showChat
											? 'bg-pink-300 cursor-default'
											: 'bg-pink-600 hover:bg-pink-700'
									} ${(!canChat && !showChat) ? 'opacity-30 cursor-not-allowed hover:bg-pink-600' : ''}`}
								>
									<FaComments className="w-3 h-3 mr-1.5" />
									{chatLabel}
									{alreadyChatted && !showChat && (
										<span className="ml-1.5 px-1 py-0.5 bg-pink-500 rounded text-[9px] font-bold uppercase tracking-wider">Resume</span>
									)}
								</button>
							</Tooltip>
						</div>

						{showSummary && (
							<div className="mb-4 accent-line pl-4 py-2">
								<StreamingSummary
									paperId={paper.id}
									onDone={handleSummaryDone}
								/>
							</div>
						)}

						{showChat && (
							<div>
								<ChatPanel paperId={paper.id} />
							</div>
						)}
					</div>

					{/* Actions */}
					<div className="flex flex-wrap gap-2.5 pt-6 border-t border-gray-100">
						<Link
							to={from}
							className="inline-flex items-center bg-gray-100 text-gray-600 px-3.5 py-2 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors"
						>
							<ArrowLeftIcon className="w-3.5 h-3.5 mr-1.5" />
							Back
						</Link>
						{isFavorite
							? renderButton(handleRemove, <TrashIcon className="w-3.5 h-3.5" />, 'Remove', 'bg-red-500 hover:bg-red-600', !isLoggedIn ? 'Login required' : '')
							: renderButton(handleAdd, <BookmarkIcon className="w-3.5 h-3.5" />, 'Save', 'bg-emerald-600 hover:bg-emerald-700', !isLoggedIn ? 'Login required' : eligibility.favoritePaperNotAllowedReason)
						}
					</div>
				</div>
			</div>
		</div>
	);
}

export default PaperInformation;
